import type { ValidationConfig } from '../validationEngine/types';
import { isVinValid } from '@interstate/components/Validation/utils';
import { addressValidationConfig } from './addressValidationConfig';
import { getRules, withoutRequiredRules, withoutContractRules, withReplacedWhenCondition } from './validationConfigRuleUtils';
import { transformValidationConfig } from '../validationEngine/transformValidationConfig';

type WhenFn = (ctx: any, value?: unknown, scope?: unknown) => boolean;

const hasNonEmptyValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

const isOutsideLienholderDetailsBundleTouched: WhenFn = (_ctx, _value, scope) => {
    if (!scope || typeof scope !== 'object') return false;
    const scoped: any = scope as any;
    const lienholderScope = scoped.lienholder && typeof scoped.lienholder === 'object' ? scoped.lienholder : scoped;

    return (
        hasNonEmptyValue(lienholderScope.accountNumber) ||
        hasNonEmptyValue(lienholderScope.streetAddress) ||
        hasNonEmptyValue(lienholderScope.city) ||
        hasNonEmptyValue(lienholderScope.state) ||
        hasNonEmptyValue(lienholderScope.zip)
    );
};

const isLienholderDetailsBundleTouched: WhenFn = (_ctx, _value, scope) => {
    if (!scope || typeof scope !== 'object') return false;
    const scoped: any = scope as any;
    const lienholderScope = scoped.lienholder && typeof scoped.lienholder === 'object' ? scoped.lienholder : scoped;

    // If OptionalDesking is active, make require fields as optional
    if (_ctx?.dealMode === 'OptionalDesking') {
        return false;
    }

    return (
        hasNonEmptyValue(lienholderScope.accountNumber) ||
        hasNonEmptyValue(lienholderScope.streetAddress) ||
        hasNonEmptyValue(lienholderScope.city) ||
        hasNonEmptyValue(lienholderScope.state) ||
        hasNonEmptyValue(lienholderScope.zip)
    );
};

const tradeInVehicleGroupConfig: ValidationConfig = {
    year: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Year is required for trade-in.',
                conditions: {
                    when: (ctx, value, tradeIn) => {
                        const vin = (tradeIn as any)?.vin;
                        return Boolean(vin && isVinValid(vin)) || hasNonEmptyValue(value);
                    }
                }
            }
        ]
    },
    make: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Make is required for trade-in.',
                conditions: {
                    when: (ctx, value, tradeIn) => {
                        const scope = tradeIn as any;
                        const year = scope?.year ?? scope?.vehicle?.year;
                        return hasNonEmptyValue(year);
                    }
                }
            }
        ]
    },
    model: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Model is required for trade-in.',
                conditions: {
                    when: (ctx, value, tradeIn) => {
                        const scope = tradeIn as any;
                        const make = scope?.make ?? scope?.vehicle?.make;
                        return hasNonEmptyValue(make);
                    }
                }
            }
        ]
    },
    trim: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Trim is required for trade-in.',
                conditions: {
                    when: (ctx, value, tradeIn) => {
                        const scope = tradeIn as any;
                        const model = scope?.model ?? scope?.vehicle?.model;
                        return hasNonEmptyValue(model);
                    }
                }
            }
        ]
    }
};

export const lienholderGroupConfig: ValidationConfig = {
    name: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Lienholder name is required if the payoff amount exceeds $0.',
                conditions: {
                    when: (ctx, value, tradeIn) => {
                        const amountOwed = (tradeIn as any)?.amountOwed ?? 0;
                        return Number(amountOwed) > 0;
                    }
                }
            },
            {
                type: 'MAX_LENGTH',
                params: { max: 50 },
                messages: `Lienholder must be under 50 characters`
            }
        ]
    },
    accountNumber: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Required',
                conditions: {
                    when: isLienholderDetailsBundleTouched
                }
            },
            {
                type: 'MAX_LENGTH',
                params: { max: 20 },
                messages: 'Number must be under 20 characters'
            }
        ]
    },
    streetAddress: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Required',
                conditions: {
                    when: isLienholderDetailsBundleTouched
                }
            },
            ...withoutRequiredRules(getRules((addressValidationConfig as any).streetAddress))
        ]
    },
    city: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Required',
                conditions: {
                    when: isLienholderDetailsBundleTouched
                }
            },
            ...withoutRequiredRules(getRules((addressValidationConfig as any).city))
        ]
    },
    state: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Required',
                conditions: {
                    when: isLienholderDetailsBundleTouched
                }
            },
            ...withoutRequiredRules(getRules((addressValidationConfig as any).state))
        ]
    },
    zip: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Required',
                conditions: {
                    when: isLienholderDetailsBundleTouched
                }
            },
            ...withoutRequiredRules(getRules((addressValidationConfig as any).zip))
        ]
    }
};

const withOutsideLienholderWhen = (fieldConfig: any) =>
    withReplacedWhenCondition(withoutContractRules(getRules(fieldConfig)), isOutsideLienholderDetailsBundleTouched);

export const lienholderGroupValidationConfig: ValidationConfig = transformValidationConfig(lienholderGroupConfig, {
    fieldLabels: {
        streetAddress: 'Bank Address'
    }
});

export const outsideLienholderValidationConfig: ValidationConfig = {
    lienholder: {
        $group: {
            name: { rules: withoutContractRules(getRules(lienholderGroupValidationConfig.name)) },
            accountNumber: { rules: withOutsideLienholderWhen(lienholderGroupValidationConfig.accountNumber) },
            streetAddress: { rules: withOutsideLienholderWhen(lienholderGroupValidationConfig.streetAddress) },
            city: { rules: withOutsideLienholderWhen(lienholderGroupValidationConfig.city) },
            state: { rules: withOutsideLienholderWhen(lienholderGroupValidationConfig.state) },
            zip: { rules: withOutsideLienholderWhen(lienholderGroupValidationConfig.zip) }
        }
    }
};

export const tradeInValidationConfig: ValidationConfig = {
    vehicle: {
        $group: tradeInVehicleGroupConfig,
        conditions: {
            when: (_ctx, _value, tradeIn) => {
                const vin = (tradeIn as any)?.vin;
                return Boolean(vin && isVinValid(vin));
            }
        }
    },
    lienholder: { $group: lienholderGroupValidationConfig }
};
